import { ScorecardCategory } from "../taxonomy/category";
import {
  EnforcementLevel,
  enforcementOfMechanisms,
} from "../taxonomy/enforcement";

/** A control parameter's value, e.g. a threshold. @public */
export type ControlParameterValue = string | number | boolean;

/**
 * A requirement a standard declares. The check with the same id measures it;
 * with no such check it is self-attested, not automated.
 * @public
 */
export interface StandardControl {
  id: string;
  /** Short name; a check is named `<standard title>: <control title>`. */
  title: string;
  /** May name a parameter in backticks, e.g. "at least `min_pages` pages". */
  statement: string;
  category: ScorecardCategory;
  /** General, tool-agnostic fix guidance. */
  howToFix: string;
  fixUrl?: string;
  /** For example proposed or active; only an active control is enforced. */
  status: string;
  /** Thresholds the statement names, by snake_case name. */
  parameters?: Readonly<Record<string, ControlParameterValue>>;
}

/** @public */
export const ACTIVE_CONTROL_STATUS = "active";

/**
 * A control's enforcement: an active control takes its standard's mechanisms
 * (see enforcementOfMechanisms); any other status is not enforced.
 * @public
 */
export function enforcementOfControl(
  standard: Pick<StandardSummary, "enforcementMechanisms">,
  control: Pick<StandardControl, "status">
): EnforcementLevel {
  return control.status === ACTIVE_CONTROL_STATUS
    ? enforcementOfMechanisms(standard.enforcementMechanisms)
    : "none";
}

/**
 * `text` with each backticked parameter name, e.g. `` `min_pages` ``,
 * replaced by its value. Names with no value are left as they are.
 * @public
 */
export function substituteParameters(
  text: string,
  parameters: Readonly<Record<string, ControlParameterValue>> | undefined
): string {
  return text.replace(/`([a-z][a-z0-9_]*)`/g, (match, name: string) =>
    parameters && Object.prototype.hasOwnProperty.call(parameters, name)
      ? String(parameters[name])
      : match
  );
}

/** A standard from the blackboard-standards registry. @public */
export interface StandardSummary {
  id: string;
  title: string;
  /** For example draft, proposed, active. */
  status: string;
  owner?: string;
  /** ISO date (YYYY-MM-DD). */
  lastReviewed?: string;
  /** The standard's page. */
  url: string;
  /** For example ci, review, manual. */
  enforcementMechanisms: string[];
  /** Absent when the standard declares none. */
  controls?: StandardControl[];
}

/** Body of `GET /api/front-door/standards`. @public */
export interface StandardsResponse {
  standards: StandardSummary[];
  /** When the registry was last read; absent before the first successful read. */
  fetchedAt?: string;
}

/** @public */
export const FRONT_DOOR_STANDARDS_PATH = "/standards";

/** Summaries by exact id; the first of a repeated id wins. @public */
export function indexStandards(
  standards: ReadonlyArray<StandardSummary> | undefined
): Map<string, StandardSummary> {
  const index = new Map<string, StandardSummary>();
  for (const standard of standards ?? []) {
    if (!index.has(standard.id)) index.set(standard.id, standard);
  }
  return index;
}

/** A control and the standard that declares it. @public */
export interface StandardControlRef {
  standard: StandardSummary;
  control: StandardControl;
}

/**
 * Check fields that come from the registry control a check measures, never
 * from the measurement: `name` and `description` at the top level, the rest
 * in `metadata`.
 * @public
 */
export const REGISTRY_OWNED_CHECK_FIELDS = Object.freeze([
  "name",
  "description",
  "standardId",
  "enforcement",
  "category",
  "howToFix",
  "controlStatus",
  "parameters",
] as const);

/** Controls by id, in registry order; the first of a repeated id wins. @public */
export function indexControls(
  standards: ReadonlyArray<StandardSummary> | undefined
): Map<string, StandardControlRef> {
  const index = new Map<string, StandardControlRef>();
  for (const standard of standards ?? []) {
    for (const control of standard.controls ?? []) {
      if (!index.has(control.id)) index.set(control.id, { standard, control });
    }
  }
  return index;
}

/**
 * The registry fields of the check that measures `control`, with parameter
 * values substituted into the statement. `fixUrl` is the control's own; a
 * measurement's fix link takes precedence over it.
 * @public
 */
export function controlCheckFields({ standard, control }: StandardControlRef): {
  name: string;
  description: string;
  metadata: {
    standardId: string;
    enforcement: EnforcementLevel;
    category: ScorecardCategory;
    howToFix: string;
    controlStatus: string;
    parameters?: Readonly<Record<string, ControlParameterValue>>;
    fixUrl?: string;
  };
} {
  return {
    name: `${standard.title}: ${control.title}`,
    description: substituteParameters(control.statement, control.parameters),
    metadata: {
      standardId: standard.id,
      enforcement: enforcementOfControl(standard, control),
      category: control.category,
      howToFix: substituteParameters(control.howToFix, control.parameters),
      controlStatus: control.status,
      ...(control.parameters ? { parameters: control.parameters } : {}),
      ...(control.fixUrl ? { fixUrl: control.fixUrl } : {}),
    },
  };
}

/** Controls with no check among `checkIds`, in registry order. @public */
export function unmeasuredControls(
  standards: ReadonlyArray<StandardSummary> | undefined,
  checkIds: Iterable<string>
): StandardControlRef[] {
  const measured = new Set(checkIds);
  return [...indexControls(standards).values()].filter(
    ({ control }) => !measured.has(control.id)
  );
}
